/**
 * The sign-in steps that are decisions rather than pixels. Pure over injected
 * ports and free of native imports, so they are tested in Node against a real
 * `ApiClient` over a memory token store (`__tests__/sign-in.test.ts`).
 */
import type { OnboardingProgress } from "@repo/contract/training";
import { z } from "zod";
import { NEW_ACCOUNT_PROGRESS } from "./gate";

export const CODE_LENGTH = 6;

/**
 * Between two "Reenviar código" taps. The server's identifier-keyed limiter
 * is the real guard (T31); this keeps a thumb from spending its budget.
 */
export const RESEND_COOLDOWN_MS = 30_000;

const email = z.email();

/** The address as sent: trimmed and lowercased, or `null` when it is not one. */
export function normalizeEmail(input: string): string | null {
  const trimmed = input.trim().toLowerCase();
  return email.safeParse(trimmed).success ? trimmed : null;
}

/**
 * Digits only, at most six. A pasted "123 456", or a code autofilled from an
 * SMS-style "Seu código: 123456", still lands as the six digits.
 */
export function normalizeCode(input: string): string {
  return input.replace(/\D/g, "").slice(0, CODE_LENGTH);
}

export function isCompleteCode(code: string): boolean {
  return code.length === CODE_LENGTH;
}

/** Whole seconds until "Reenviar código" is allowed again; 0 when it is. */
export function resendSecondsLeft(sentAt: number, now: number): number {
  return Math.max(0, Math.ceil((sentAt + RESEND_COOLDOWN_MS - now) / 1000));
}

export interface CompleteSignInPorts {
  /**
   * `accountScope.enter`: this device's data belongs to this account from
   * here on. When a DIFFERENT account was current, its cleanup runs inside
   * this call (T35).
   */
  enterAccount(accountId: string): Promise<void>;
  /** `GET /me/onboarding` into the Query cache (a `fetchQuery`). */
  fetchOnboarding(): Promise<OnboardingProgress>;
  /** `useSession().refresh()`: re-read the token store so the gate flips. */
  refresh(): Promise<void>;
  /** Write a known answer into the onboarding query's cache. */
  seedOnboarding(progress: OnboardingProgress): void;
  /** `ApiClient.submitCode`, which stores the token pair. */
  submitCode(
    verificationId: string,
    code: string
  ): Promise<{ accountId: string; isNewAccount: boolean }>;
}

/**
 * Verify the code, then make the gate's next decision already answerable
 * BEFORE the session flips, so the person lands on the stepper or on Today
 * with no blank frame between:
 *
 * - a new account has answered nothing, so its progress is seeded, not
 *   fetched;
 * - an existing account's progress is fetched; a failure there is not a
 *   failed sign-in (the tokens are stored), so it is swallowed and the gate
 *   decides from the query as it would on a cold start.
 *
 * `enterAccount` runs FIRST, right after the code was accepted: it is what
 * makes the device this account's, and when a different person was signed in
 * here it is what forgets their local data (T34/T35). Everything after it —
 * the progress read, the gate flipping, the drain `onSignedIn` triggers —
 * would otherwise see the previous account's data or send its queued writes
 * under this one.
 *
 * `refresh()` runs last and always: without it the session context keeps the
 * signed-out state until the next reload (T11).
 */
export async function completeSignIn(
  ports: CompleteSignInPorts,
  verificationId: string,
  code: string
): Promise<{ isNewAccount: boolean }> {
  const { accountId, isNewAccount } = await ports.submitCode(
    verificationId,
    code
  );
  await ports.enterAccount(accountId);
  if (isNewAccount) {
    ports.seedOnboarding(NEW_ACCOUNT_PROGRESS);
  } else {
    await ports.fetchOnboarding().catch((error: unknown) => {
      console.warn("[auth] onboarding progress unavailable at sign-in", error);
    });
  }
  await ports.refresh();
  return { isNewAccount };
}
